import styled, { css } from 'styled-components';

import { rem } from '../../tokens/spacing';
import { palette } from '../../tokens/palette';
import { mediaQuery, breakpoints } from '../../tokens/breakpoints';
import { duration } from '../../tokens/motion';

/* ─────────────────────────── Transient prop filters ────────────────────── */

const SHELL_MAIN_TRANSIENTS = ['$bgImage', '$padding', '$variant'] as const;
const shouldForwardMain = (prop: string | number): boolean =>
  !SHELL_MAIN_TRANSIENTS.includes(prop as (typeof SHELL_MAIN_TRANSIENTS)[number]);

const SHELL_SIDEBAR_TRANSIENTS = ['$width', '$collapsed'] as const;
const shouldForwardSidebar = (prop: string | number): boolean =>
  !SHELL_SIDEBAR_TRANSIENTS.includes(prop as (typeof SHELL_SIDEBAR_TRANSIENTS)[number]);

/* ─────────────────────────── Shell Root ─────────────────────────────────── */

export const ShellRoot = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
`;

/* ─────────────────────────── Banner Slot ─────────────────────────────────── */

export const ShellBannerSlot = styled.div`
  flex-shrink: 0;
  width: 100%;
`;

/* ─────────────────────────── Navbar Spacer ───────────────────────────────── */

export const ShellNavbarSpacer = styled.div`
  height: var(--shell-navbar-h, ${rem(67)});
  flex-shrink: 0;
`;

/* ─────────────────────────── Body (flex row) ─────────────────────────────── */

export const ShellBody = styled.div`
  display: flex;
  flex: 1;
  position: relative;
`;

/* ─────────────────────────── Sidebar wrapper ─────────────────────────────── */

export const ShellSidebarSlot = styled.div.withConfig({
  shouldForwardProp: shouldForwardSidebar,
})<{
  $width: string;
  $collapsed: boolean;
}>`
  width: ${({ $width }) => $width};
  flex-shrink: 0;
  transition: width ${duration.normal}ms cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;

  @media (max-width: ${breakpoints.md - 1}px) {
    width: 0;
  }
`;

/* ─────────────────────────── Main content area ──────────────────────────── */

export type MainPadding = 'none' | 'standard' | 'compact';
export type ShellVariant = 'default' | 'minimal' | 'dashboard';

const paddingStyles: Record<MainPadding, ReturnType<typeof css>> = {
  none: css``,
  standard: css`
    padding: ${rem(20)};
    ${mediaQuery.md} {
      padding: ${rem(37)};
    }
    @media (min-width: 1200px) {
      padding: ${rem(43)} ${rem(120)};
    }
  `,
  compact: css`
    padding: ${rem(16)};
    ${mediaQuery.md} {
      padding: ${rem(24)};
    }
  `,
};

export const ShellMainRoot = styled.main.withConfig({
  shouldForwardProp: shouldForwardMain,
})<{
  $bgImage?: string;
  $padding: MainPadding;
  $variant: ShellVariant;
}>`
  flex: 1;
  min-width: 0;
  position: relative;
  min-height: calc(100vh - var(--shell-navbar-h, ${rem(67)}));

  ${({ $padding }) => paddingStyles[$padding]}

  ${({ $variant }) =>
    $variant === 'dashboard' &&
    css`
      max-width: ${rem(1280)};
      margin-left: auto;
      margin-right: auto;
      width: 100%;
    `}

  ${({ $bgImage }) =>
    $bgImage != null &&
    $bgImage !== '' &&
    css`
      &::before {
        content: '';
        position: fixed;
        top: var(--shell-navbar-h, ${rem(67)});
        left: 0;
        right: 0;
        bottom: 0;
        z-index: -1;
        background: url(${$bgImage}) no-repeat;
        background-size: cover;
        height: calc(100vh - var(--shell-navbar-h, ${rem(67)}));
        pointer-events: none;
      }
    `}
`;

/* ─────────────────────────── Footer slot ─────────────────────────────────── */

export const ShellFooterSlot = styled.div`
  margin-top: auto;
`;

/* ─────────────────────────── Overlay (modals) ───────────────────────────── */

export const ShellOverlay = styled.div`
  position: relative;
`;
